import {produce} from 'immer'

import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {BS} from '../../../../types/BS_types'
import {emptyObject} from '../../../../utils/empty'

import type {
  MatrixParamBuildErrorContextProps,
  MatrixParamBuildErrorProviderProps,
} from './MatrixParamBuildErrorContext.types'

const MatrixParamBuildErrorContext = createContext<MatrixParamBuildErrorContextProps>(
  {} as MatrixParamBuildErrorContextProps,
)

export const useMatrixParamBuildError = () => useContext(MatrixParamBuildErrorContext)

function MatrixParamBuildErrorProvider({
  children,
  externalErrors,
  isReadOnlyMode,
}: MatrixParamBuildErrorProviderProps) {
  const hideFieldErrorsRef = useRef<Record<string, boolean>>({})
  const externalErrorsRef = useRef<Map<string, string>>()
  const [fieldErrorMessage, setFieldErrorMessage] =
    useState<Record<string, string | null>>(emptyObject)
  const deleteErrorMessageHandler = useCallback(
    (parameterIndex: number) => {
      const fieldKey = `[${parameterIndex}]`

      setFieldErrorMessage(state =>
        produce(state, draft => {
          Object.keys(fieldErrorMessage).forEach(errorKey => {
            const isIncludeKey = errorKey.startsWith(fieldKey)

            if (isIncludeKey) {
              delete draft[errorKey]
            }
          })
        }),
      )

      hideFieldErrorsRef.current = Object.keys(hideFieldErrorsRef.current).reduce<
        Record<string, boolean>
      >((accumulator, errorKey) => {
        const isIncludeKey = errorKey.startsWith(fieldKey)

        accumulator[errorKey] = !isIncludeKey ? hideFieldErrorsRef.current[errorKey] : false

        return accumulator
      }, {})

      externalErrorsRef.current?.clear()
    },
    [fieldErrorMessage],
  )
  const setErrorMessageHandler = useCallback(
    (error: string | null, key: string, isManuallyChanged?: boolean) => {
      const currentError = fieldErrorMessage[key]
      const hasExternalError = externalErrorsRef.current?.has(key)

      // Remove all external errors if something changes in form
      if (externalErrorsRef.current?.size && isManuallyChanged) {
        const externalErrorsObject = [...externalErrorsRef.current.keys()]

        setFieldErrorMessage(state =>
          produce(state, draft => {
            externalErrorsObject.forEach(fieldPath => {
              delete draft[fieldPath]
            })
          }),
        )
      }

      if (isManuallyChanged || !hasExternalError) {
        if (hasExternalError && isManuallyChanged) {
          externalErrorsRef.current?.delete(key)
        }

        if (currentError || error) {
          setFieldErrorMessage(state =>
            produce(state, draft => {
              draft[key] = error
            }),
          )
        }
      }
    },
    [fieldErrorMessage],
  )
  const setHideErrorHandler = useCallback(
    (fieldPath: string, state: boolean) => (hideFieldErrorsRef.current[fieldPath] = state),
    [],
  )
  const hasFieldsHideErrorHandler = useCallback(() => {
    const errors = Object.keys(hideFieldErrorsRef.current).filter(
      key => hideFieldErrorsRef.current[key],
    )

    setFieldErrorMessage(state => ({
      ...state,
      ...errors.reduce((accumulator, field) => ({...accumulator, [field]: null}), {}),
    }))

    return Boolean(errors.length)
  }, [])
  const contextValue = useMemo(
    () => ({
      fieldErrorMessage,
      setHideErrorHandler,
      setErrorMessageHandler,
      deleteErrorMessageHandler,
      hasFieldsHideErrorHandler,
    }),
    [
      deleteErrorMessageHandler,
      fieldErrorMessage,
      hasFieldsHideErrorHandler,
      setErrorMessageHandler,
      setHideErrorHandler,
    ],
  )

  useEffect(() => {
    if (externalErrors.size) {
      const externalErrorsObject = Object.fromEntries(externalErrors.entries())

      setFieldErrorMessage(state => ({
        ...state,
        ...externalErrorsObject,
      }))

      externalErrorsRef.current = externalErrors
    }
  }, [externalErrors])

  useEffect(() => {
    const errorLength = Object.keys(fieldErrorMessage).filter(
      key => !!fieldErrorMessage[key],
    ).length
    const isDisabledSaveButton = Boolean(!!errorLength || isReadOnlyMode)

    BS?.BuildFeatureDialog?.setSaveButtonDisabled(isDisabledSaveButton)
  }, [fieldErrorMessage, isReadOnlyMode])

  return (
    <MatrixParamBuildErrorContext.Provider value={contextValue}>
      {children}
    </MatrixParamBuildErrorContext.Provider>
  )
}

export default memo<MatrixParamBuildErrorProviderProps>(MatrixParamBuildErrorProvider)
